import React, { useMemo } from 'react';
import { useSecondScreenData } from '../../../contexts/SecondScreenContext';
import type { WidgetProps } from '../../../types/second-screen.types';

const PulseIndicatorWidget: React.FC<WidgetProps> = () => {
  const { state, theme } = useSecondScreenData();
  const isDark = theme === 'dark';

  // Calculate activity rate: entries per minute in the last 5 minutes
  const { rate, level } = useMemo(() => {
    const transcript = state?.transcript ?? [];
    const now = Date.now();
    const fiveMinAgo = now - 5 * 60 * 1000;
    const recentEntries = transcript.filter(e => {
      const ts = new Date(e.timestamp).getTime();
      return ts >= fiveMinAgo;
    });
    const entriesPerMinute = recentEntries.length / 5;

    let level: 'dormant' | 'calm' | 'active' | 'intense';
    if (entriesPerMinute < 0.5) level = 'dormant';
    else if (entriesPerMinute < 2) level = 'calm';
    else if (entriesPerMinute < 5) level = 'active';
    else level = 'intense';

    return { rate: entriesPerMinute, level };
  }, [state?.transcript]);

  if (!state) return null;

  const pulseConfig = {
    dormant: { duration: '3s', color: 'rgba(0,199,196,0.3)', ringColor: 'rgba(0,199,196,0.1)', label: 'Dormant' },
    calm:    { duration: '2s', color: 'rgba(0,199,196,0.5)', ringColor: 'rgba(0,199,196,0.15)', label: 'Calm' },
    active:  { duration: '1s', color: 'rgba(0,199,196,0.7)', ringColor: 'rgba(0,199,196,0.25)', label: 'Active' },
    intense: { duration: '0.5s', color: 'rgba(0,199,196,0.9)', ringColor: 'rgba(0,199,196,0.35)', label: 'Intense' },
  };

  const config = pulseConfig[level];

  return (
    <div className="flex flex-col items-center justify-center h-full gap-2">
      {/* Pulsing circle */}
      <div className="relative flex items-center justify-center">
        {/* Outer pulse ring */}
        <div
          className="absolute w-20 h-20 rounded-full animate-ping"
          style={{
            backgroundColor: config.ringColor,
            animationDuration: config.duration,
          }}
        />
        {/* Second pulse ring */}
        <div
          className="absolute w-16 h-16 rounded-full animate-ping"
          style={{
            backgroundColor: config.ringColor,
            animationDuration: config.duration,
            animationDelay: '0.2s',
          }}
        />
        {/* Core circle with rate */}
        <div
          className="relative w-14 h-14 rounded-full flex items-center justify-center z-10"
          style={{ backgroundColor: config.color }}
        >
          <span className={`text-lg font-bold font-mono ${isDark ? 'text-white' : 'text-navy'}`}>
            {rate.toFixed(1)}
          </span>
        </div>
      </div>

      {/* Label */}
      <div className="text-center">
        <span className={`text-xs font-semibold uppercase tracking-wider ${
          isDark ? 'text-white/40' : 'text-slate-400'
        }`}>
          {config.label} · {rate.toFixed(1)}/min
        </span>
      </div>
    </div>
  );
};

export default PulseIndicatorWidget;
